/**
 * Página pública de validação de documento (leitura de QR code).
 * URL: /validar/:codigo
 *
 * Confirma que o documento é válido e emitido pelo CEAP BRASIL.
 * Não requer login. Formato do código: RP-... (Relação de Participantes) ou LP-... (Lista de Presença).
 */
import { useParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck, FileText, Users } from "lucide-react";

const CEAP_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/b4qjckp1_nova_logo_CEAP_2020-removebg-preview.png";

function _parseCodigo(codigo) {
  // Formatos:
  //  RP-CEAP-2026-0347-015  → Relação de Participantes
  //  LP-CEAP-2026-0347-D18M → Lista de Presença
  if (!codigo) return null;
  const [tipo, ...rest] = codigo.split("-");
  const label =
    tipo === "RP" ? "Relação de Participantes" :
    tipo === "LP" ? "Lista de Presença" :
    "Documento CEAP";
  const cursoCodigo = rest.slice(0, 3).join("-"); // CEAP-2026-0347
  const extra = rest.slice(3).join("-");
  return { tipo, label, cursoCodigo, extra };
}

export default function ValidarDocumento() {
  const { codigo } = useParams();
  const info = _parseCodigo(codigo);
  const valido = !!info && ["RP", "LP"].includes(info.tipo);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f5673] via-[#1d7495] to-[#0f5673] flex items-center justify-center p-4" data-testid="validar-page">
      <div className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Faixa CEAP */}
        <div className="bg-[#1d7495] px-8 py-6 flex items-center gap-4">
          <img
            src={CEAP_LOGO}
            alt="CEAP Brasil"
            className="h-14 object-contain"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <div className="text-white">
            <div className="text-[10px] uppercase tracking-widest opacity-80">Validação de documento</div>
            <div className="text-sm font-semibold">Centro de Estudos da Administração Pública</div>
          </div>
        </div>

        {/* Status */}
        <div className="px-8 py-8">
          {valido ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Documento válido</div>
                  <div className="text-2xl font-bold text-slate-900">Emitido oficialmente pelo CEAP BRASIL</div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                    Tipo de documento
                  </div>
                  <div className="flex items-center gap-2 text-slate-900 font-bold text-lg">
                    {info.tipo === "RP" ? <Users className="w-5 h-5 text-[#1d7495]" /> : <FileText className="w-5 h-5 text-[#1d7495]" />}
                    {info.label}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Código do curso
                    </div>
                    <div className="text-slate-900 font-bold" data-testid="curso-codigo">
                      {info.cursoCodigo || "—"}
                    </div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
                      Código de validação
                    </div>
                    <div className="text-slate-900 font-mono font-bold text-sm break-all" data-testid="codigo-validacao">
                      {codigo}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900 leading-relaxed">
                  Este documento é <b>autêntico</b> e foi emitido oficialmente pelo{" "}
                  <b>CEAP BRASIL — Centro de Estudos da Administração Pública</b>.
                  A autenticidade foi verificada eletronicamente através do QR code impresso no documento.
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-bold">Código não reconhecido</div>
                  <div className="text-xl font-bold text-slate-900">Verificação inconclusiva</div>
                </div>
              </div>
              <p className="text-slate-600 text-sm">
                O código informado (<span className="font-mono">{codigo}</span>) não segue o formato esperado.
                Verifique se você escaneou o QR code correto do documento oficial CEAP BRASIL.
              </p>
            </>
          )}
        </div>

        {/* Rodapé */}
        <div className="bg-slate-50 border-t border-slate-200 px-8 py-4 text-center text-xs text-slate-500">
          CEAP Brasil · Centro de Estudos da Administração Pública
          <br />
          <a href="/" className="text-[#1d7495] hover:underline font-semibold">Voltar ao sistema</a>
        </div>
      </div>
    </div>
  );
}
